import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ReferralContextValue } from 'hooks/useReferralContext';
import { getBrandColor } from 'utils/brandColor';

interface Props {
  context: ReferralContextValue;
  /**
   * Id du conteneur du formulaire d'inscription en bas du hero. Le CTA y
   * scrolle smoothly quand cliqué. Si omis, le CTA scrolle simplement vers
   * le bas du document.
   */
  scrollTargetId?: string;
}

const MotionBox = motion(Box);

/**
 * Hero d'accueil affiché en haut de /account quand un filleul arrive via
 * un lien de parrainage. Lit toute sa copy depuis `context.welcomeCopy`
 * (mergée campaign override + welcomeCopyDefaults côté serveur), tombe
 * sur des fallbacks i18n quand un champ est vide.
 */
export function ReferralWelcome({ context, scrollTargetId }: Props) {
  const { t } = useTranslation();

  if (!context.hasReferral) return null;

  const primaryColor = getBrandColor();
  const brandName = mondayMindbody?.customization?.brandName ?? 'Monday Sports Club';

  const copy = context.welcomeCopy;
  const campaign = context.campaign;

  // Récompense filleul (si activée). Utilisée pour résoudre les tokens
  // {{refereeCreditQuantity}} dans la copy admin et pour choisir entre
  // l'offre filleul vs. la mention "votre ami a tout à gagner".
  const refereeQuantity =
    campaign?.refereeCreditEnabled && campaign.refereeCreditQuantity
      ? campaign.refereeCreditQuantity
      : 0;

  const replaceTokens = (s: string | undefined | null): string => {
    if (!s) return '';
    return s
      .replace(/\{\{\s*referrerName\s*\}\}/gi, context.referrerFirstName ?? '')
      .replace(/\{\{\s*refereeCreditQuantity\s*\}\}/gi, String(refereeQuantity))
      .replace(/\{\{\s*brandName\s*\}\}/gi, brandName);
  };

  const titleResolved = replaceTokens(copy?.title) || t('referral_welcome.fallback_title');

  const subtitleRaw = replaceTokens(copy?.subtitle);
  const subtitleResolved =
    subtitleRaw && subtitleRaw.replace(/\s+/g, ' ').trim() !== ''
      ? subtitleRaw
      : context.referrerFirstName
        ? t('referral_welcome.fallback_subtitle_named', { name: context.referrerFirstName })
        : t('referral_welcome.fallback_subtitle_anonymous');

  const offerHeadlineResolved =
    replaceTokens(copy?.offerHeadline) || t('referral_welcome.fallback_offer_headline');

  const offerBodyResolved = (() => {
    const fromCopy = replaceTokens(copy?.offerBody);
    if (fromCopy && fromCopy.trim() !== '') return fromCopy;
    if (refereeQuantity > 0) {
      return t('referral_welcome.fallback_offer_body_referee', { count: refereeQuantity });
    }
    return t('referral_welcome.fallback_offer_body_referrer_only');
  })();

  // Fallback PER STEP — pas tout-ou-rien. Si l'admin remplit seulement step
  // 2 et laisse 1+3 vides, on rend step 2 perso + step 1 et 3 i18n. Le bug
  // précédent (`fromCopy.every(...)`) faisait basculer les 3 steps sur i18n
  // dès qu'un seul était vide → les surcharges Daphné step 2 et 3 ne
  // s'appliquaient pas (cf. screenshot du 02/06).
  const stepFallbacks: [string, string, string] = [
    t('referral_welcome.fallback_step_1'),
    t('referral_welcome.fallback_step_2'),
    t('referral_welcome.fallback_step_3'),
  ];
  const stepsResolved: [string, string, string] = ([0, 1, 2].map((i) => {
    const fromCopy = Array.isArray(copy?.steps) ? copy?.steps[i] : undefined;
    if (typeof fromCopy === 'string' && fromCopy.trim() !== '') {
      return replaceTokens(fromCopy);
    }
    return stepFallbacks[i];
  }) as [string, string, string]);

  const ctaLabelResolved = replaceTokens(copy?.ctaLabel) || t('referral_welcome.fallback_cta');
  const heroImageUrl = copy?.heroImageUrl ?? null;
  const heroImageFit: 'cover' | 'contain' = copy?.heroImageFit ?? 'cover';
  const heroImagePosition: string = copy?.heroImagePosition ?? 'center center';
  // % de la hauteur de l'image visible AU-DESSUS du bandeau texte. 100 =
  // image entièrement visible (texte juste en dessous). Plus bas, le
  // bandeau texte remonte et recouvre les (100 - X)% du bas de l'image.
  const heroImageVisiblePercent = Math.max(
    20,
    Math.min(100, copy?.heroImageVisiblePercent ?? 100),
  );

  // L'image n'a plus de hauteur fixe : elle prend sa taille naturelle pour
  // la largeur du hero, donc une image portrait peut dépasser 220px.
  // On mesure la hauteur réellement rendue (via ResizeObserver pour suivre
  // les changements de largeur du viewport et le chargement asynchrone de
  // l'image) pour calculer la marge négative à appliquer au Stack texte.
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const [measuredImageHeight, setMeasuredImageHeight] = useState(0);
  useEffect(() => {
    if (!heroImageUrl) {
      setMeasuredImageHeight(0);
      return;
    }
    const el = imageWrapperRef.current;
    if (!el) return;
    const measure = () => setMeasuredImageHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [heroImageUrl]);
  const stackOverlapPx = (measuredImageHeight * (100 - heroImageVisiblePercent)) / 100;

  const handleCtaClick = useCallback(() => {
    if (scrollTargetId) {
      const el = document.getElementById(scrollTargetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }, [scrollTargetId]);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      mb={8}
    >
      <Box
        borderRadius="lg"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        boxShadow="md"
      >
        {/* Image en HAUT du contenu. Hauteur NON-FIXÉE : l'image prend sa
            taille naturelle pour la largeur du hero (peut donc dépasser
            la limite arbitraire 220px précédente). Pilotée depuis le BO :
            - heroImageFit (cover/contain) + heroImagePosition : utiles
              uniquement si un jour on capait la hauteur, conservés pour
              cohérence et évolution future.
            - heroImageVisiblePercent (20-100) : tire le bandeau texte
              vers le haut pour ne laisser visible que X% de la hauteur
              RÉELLEMENT RENDUE de l'image, le reste passe DERRIÈRE le
              texte (le bg white du Stack masque la partie cachée). */}
        {heroImageUrl && (
          <Box
            ref={imageWrapperRef}
            overflow="hidden"
            bg={heroImageFit === 'contain' ? 'gray.50' : undefined}
          >
            <Image
              src={heroImageUrl}
              alt=""
              w="100%"
              display="block"
              objectFit={heroImageFit}
              objectPosition={heroImagePosition}
              onLoad={() => {
                if (imageWrapperRef.current) {
                  setMeasuredImageHeight(imageWrapperRef.current.offsetHeight);
                }
              }}
            />
          </Box>
        )}

        <Stack
          p={{ base: 5, md: 8 }}
          spacing={5}
          bg="white"
          borderTop={heroImageUrl ? `4px solid ${primaryColor}` : 'none'}
          position="relative"
          zIndex={1}
          // style.marginTop direct au lieu de Chakra `mt` pour bypass tout
          // souci d'interprétation des valeurs négatives en template literal
          // (Chakra peut tenter de matcher des tokens et ignorer "-44px").
          style={
            heroImageUrl && stackOverlapPx > 0
              ? { marginTop: `-${stackOverlapPx}px` }
              : undefined
          }
        >
          <Stack spacing={2} textAlign="left">
            <Text
              fontSize="sm"
              fontWeight="700"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={primaryColor}
              textAlign="left"
            >
              {subtitleResolved}
            </Text>
            <Heading as="h2" size="lg" lineHeight="1.2" m={0} textAlign="left">
              {titleResolved}
            </Heading>
          </Stack>

          <Flex
            align="stretch"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 3, md: 5 }}
          >
            {/* Offer card */}
            <Box
              flex="1"
              p={4}
              bg={`${primaryColor}10`}
              border="1px solid"
              borderColor={`${primaryColor}40`}
              borderRadius="md"
            >
              <Stack spacing={1}>
                <HStack spacing={2}>
                  <GiftIcon color={primaryColor} />
                  <Text fontWeight="700" fontSize="sm" textTransform="uppercase" letterSpacing="0.05em">
                    {offerHeadlineResolved}
                  </Text>
                </HStack>
                <Text fontSize="md" textAlign="left">
                  {offerBodyResolved}
                </Text>
              </Stack>
            </Box>

            {/* Steps */}
            <Box flex="1.4" textAlign="left">
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={2}
                textAlign="left"
              >
                {t('referral_welcome.how_it_works')}
              </Text>
              <Stack spacing={2}>
                {stepsResolved.map((step, i) => (
                  <HStack key={i} align="start" spacing={3}>
                    <Box
                      flexShrink={0}
                      w="22px"
                      h="22px"
                      borderRadius="full"
                      bg={primaryColor}
                      color="white"
                      fontSize="xs"
                      fontWeight="700"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      lineHeight="1"
                    >
                      {i + 1}
                    </Box>
                    <Text fontSize="sm" lineHeight="1.4">
                      {step}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            </Box>
          </Flex>

          <Flex justify={{ base: 'stretch', md: 'flex-end' }}>
            <Button
              size="lg"
              w={{ base: '100%', md: 'auto' }}
              bg={primaryColor}
              color="white"
              _hover={{ bg: primaryColor, opacity: 0.92 }}
              onClick={handleCtaClick}
            >
              {ctaLabelResolved} ↓
            </Button>
          </Flex>
        </Stack>

      </Box>
    </MotionBox>
  );
}

function GiftIcon({ color }: { color: string }) {
  return (
    <Icon viewBox="0 0 24 24" boxSize={5} color={color}>
      <path
        fill="currentColor"
        d="M20 7h-2.18A3 3 0 0 0 18 6a3 3 0 0 0-5.83-1A3 3 0 0 0 6 6a3 3 0 0 0 .18 1H4a2 2 0 0 0-2 2v2a1 1 0 0 0 1 1h1v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7h1a1 1 0 0 0 1-1V9a2 2 0 0 0-2-2Zm-5-2a1 1 0 0 1 1 1 1 1 0 0 1-1 1h-2V6a1 1 0 0 1 1-1ZM9 5a1 1 0 0 1 1 1v1H9a1 1 0 0 1 0-2Zm-5 4h7v2H4V9Zm2 4h5v6H6v-6Zm12 6h-5v-6h5v6Zm2-8h-7V9h7v2Z"
      />
    </Icon>
  );
}
